import {
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow
} from "@mui/material";
import React from "react";
import BreadCrumbsHeader from "../../../components/BreadCrumbsHeader";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import UserQuery from "../../../stateQueries/User";
import TablePaginationActions from "@mui/material/TablePagination/TablePaginationActions";
import CustomNoRowsOverlay from "../../../components/CustomNoRowsOverlay";
import { DeleteBannerImageModal } from "../../../components/Modals/DeleteBannerImageModal";
import AlertPopup from "../../../components/AlertPopup";
import UploadIcon from "@mui/icons-material/Upload";

const Banners = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const { data, isLoading } = useQuery({
    queryKey: ["banners"],
    queryFn: async () => {
      return await UserQuery.CSEQuery.getAllBannerImages();
    }
  });

  const {
    mutate,
    isLoading: isUploading,
    isSuccess,
    isError,
    error,
    data: uploadData
  } = useMutation({
    mutationFn: async (formData) => {
      return await UserQuery.CSEQuery.addBannerImageFile(formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries("banners");
    }
  });

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("imageFile", file);
      mutate(formData);
    }
  };

  if (isLoading) {
    return <LinearProgress />;
  }

  return (
    <Stack spacing={2}>
      {isError && (
        <AlertPopup
          open={true}
          severity="error"
          message={error?.response?.data?.message || "Server Error"}
        />
      )}
      {isSuccess && <AlertPopup open={true} message={uploadData?.message} />}

      <BreadCrumbsHeader
        title="Welcome back Tiyisela Themba Makamu"
        menus={[
          { name: "Dashboard", url: "/dashboard" },
          { name: "Website Management", url: "/websiteManagement" },
          { name: "Banners", url: "/cse/banners" }
        ]}
        sx={{ mb: 2 }}
      />
      <Stack justifyContent="center" alignItems="center" spacing={2}>
        <Button
          variant="contained"
          component="label"
          startIcon={
            isUploading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <UploadIcon />
            )
          }
          disabled={isUploading}
          sx={{
            px: 4,
            py: 1.5,
            fontSize: "1rem",
            fontWeight: 600,
            borderRadius: 2,
            textTransform: "none",
            boxShadow: 2,
            "&:hover": {
              boxShadow: 4,
              transform: "translateY(-2px)",
              transition: "all 0.3s ease"
            },
            "&:disabled": {
              opacity: 0.7
            }
          }}
        >
          {isUploading ? "Uploading..." : "Upload Banner"}
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={handleFileChange}
          />
        </Button>

        <TableContainer component={Paper}>
          <Table aria-label="simple table">
            <TableHead sx={{ backgroundColor: "background.paper" }}>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{ fontWeight: "bolder", width: 150 }}
                >
                  No#
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bolder" }}>
                  Image
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bolder" }}>
                  Date Created
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bolder" }}>
                  Date Updated
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: "bolder" }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(rowsPerPage > 0
                ? data?.banners?.slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                  )
                : data?.banners
              )?.map((banner, i) => {
                return (
                  <TableRow key={banner.id}>
                    <TableCell align="center" component="th" scope="row">
                      {i + 1}
                    </TableCell>
                    <TableCell component="th" scope="row" align="center">
                      <img
                        src={`${process.env.REACT_APP_API_URL}/uploads/banners/${banner.bannerImageURL}`}
                        alt=""
                        height={100}
                      />
                    </TableCell>
                    <TableCell align="center" component="th" scope="row">
                      {`${new Date(banner.createdAt).toDateString()}`}
                    </TableCell>
                    <TableCell align="center" component="th" scope="row">
                      {`${new Date(banner.updatedAt).toDateString()}`}
                    </TableCell>

                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={2}
                        // border={1}
                        justifyContent="center"
                      >
                        <DeleteBannerImageModal id={banner.id} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            {data?.banners?.length > 0 && (
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[
                      5,
                      10,
                      25,
                      { label: "All", value: -1 }
                    ]}
                    // colSpan={3}
                    count={data?.banners?.length || 0}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    SelectProps={{
                      inputProps: {
                        "aria-label": "rows per page"
                      },
                      native: true
                    }}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            )}
          </Table>
          {(!data?.banners || data?.banners?.length === 0) && (
            <Stack width="100%" padding={2}>
              <CustomNoRowsOverlay description="No Banner Images Available" />
            </Stack>
          )}
        </TableContainer>
      </Stack>
    </Stack>
  );
};

export default Banners;
